import { Capacitor } from '@capacitor/core'
import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser'
import { KlunkersPasskeys } from '@klunkers/capacitor-passkeys'

/**
 * One way in to passkeys for both places the app runs (docs/passkeys.md). A browser
 * uses WebAuthn directly. The phone app's page is served from
 * https://localhost, which cannot use a passkey for the website, so it asks
 * Android's Credential Manager through a small native plugin instead. Both
 * take and give the same WebAuthn JSON.
 */
const native = () => {
  try {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android'
  } catch {
    return false
  }
}

/**
 * Which logins made a passkey on this device, so it is not offered here
 * again. A convenience kept in the browser: lost, it means one more offer.
 */
const HERE_KEY = 'klunkers.passkeyHere'

function loginsWithPasskeyHere(): string[] {
  try {
    return JSON.parse(localStorage.getItem(HERE_KEY) ?? '[]') as string[]
  } catch {
    return []
  }
}

export function hasPasskeyHere(userId: string): boolean {
  return loginsWithPasskeyHere().includes(userId)
}

export function markPasskeyHere(userId: string): void {
  try {
    localStorage.setItem(
      HERE_KEY,
      JSON.stringify([...new Set([...loginsWithPasskeyHere(), userId])]),
    )
  } catch {
    // Unavailable storage costs an extra offer, nothing more.
  }
}

/** A sensible name for the device in hand, which the person can change. */
export function deviceName(): string {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent
  const pixel = ua.match(/Pixel [^;)]+/)
  if (pixel) return pixel[0]
  if (/Android/.test(ua)) return 'Android phone'
  if (/iPhone|iPad/.test(ua)) return 'iPhone'
  if (/Mac OS X/.test(ua)) return 'Mac'
  if (/Windows/.test(ua)) return 'Windows'
  if (/Linux/.test(ua)) return 'Linux'
  return 'This device'
}

/** Somebody closed the sheet: not an error worth a red message. */
export class PasskeyCancelled extends Error {}

export async function passkeysSupported(): Promise<boolean> {
  if (native()) {
    try {
      return (await KlunkersPasskeys.isAvailable()).available
    } catch {
      return false
    }
  }
  try {
    return Capacitor.isNativePlatform() ? false : browserSupportsWebAuthn()
  } catch {
    return false
  }
}

const cancelled = (err: unknown) => {
  const e = err as { code?: string; name?: string }
  return e?.code === 'cancelled' || e?.name === 'NotAllowedError' || e?.name === 'AbortError'
}

export async function createPasskey(
  options: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  try {
    if (native()) {
      const { responseJson } = await KlunkersPasskeys.create({
        requestJson: JSON.stringify(options),
      })
      return JSON.parse(responseJson) as Record<string, unknown>
    }
    return (await startRegistration({
      optionsJSON: options as unknown as PublicKeyCredentialCreationOptionsJSON,
    })) as unknown as Record<string, unknown>
  } catch (err) {
    if (cancelled(err)) throw new PasskeyCancelled()
    throw err
  }
}

export async function getPasskey(
  options: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  try {
    if (native()) {
      const { responseJson } = await KlunkersPasskeys.get({ requestJson: JSON.stringify(options) })
      return JSON.parse(responseJson) as Record<string, unknown>
    }
    return (await startAuthentication({
      optionsJSON: options as unknown as PublicKeyCredentialRequestOptionsJSON,
    })) as unknown as Record<string, unknown>
  } catch (err) {
    if (cancelled(err)) throw new PasskeyCancelled()
    throw err
  }
}
